import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useToast } from '../../context/ToastContext';

interface Checkpoint {
  id: string;
  name: string;
  location: string;
  completed: boolean;
  completedAt?: string;
  overdue?: boolean;
}

interface Tour {
  id: string;
  officer: string;
  site: string;
  startTime: string;
  endTime?: string;
  status: 'in-progress' | 'completed' | 'scheduled' | 'overdue' | 'missed';
  completionPct: number;
  checkpoints: Checkpoint[];
  createdAt: string;
  createdBy?: string;
  notes?: string;
}

const SITES = ['Town Hall', 'Civic Centre', 'Aquatic Centre', 'Council Depot', 'Parramatta Square', 'River Walk'];

const STATUS_COLOR: Record<string, string> = {
  'in-progress': '#3b82f6', completed: '#22c55e', scheduled: '#555555',
  overdue: '#f97316', missed: '#ef4444',
};
const STATUS_BG: Record<string, string> = {
  'in-progress': 'rgba(59,130,246,0.1)', completed: 'rgba(34,197,94,0.1)',
  scheduled: 'rgba(100,116,139,0.1)', overdue: 'rgba(249,115,22,0.1)', missed: 'rgba(239,68,68,0.1)',
};

const CHECKPOINT_PRESETS: Record<string, { name: string; location: string }[]> = {
  'Town Hall': [
    { name: 'Main Entry', location: 'Ground Floor' },
    { name: 'Server Room', location: 'Basement' },
    { name: 'Council Chamber', location: 'Level 2' },
    { name: 'Car Park Entry', location: 'Basement' },
    { name: 'Roof Access', location: 'Level 4' },
    { name: 'Loading Dock', location: 'Rear' },
  ],
  'Civic Centre': [
    { name: 'Main Lobby', location: 'Ground Floor' },
    { name: 'Level 2 Offices', location: 'Level 2' },
    { name: 'Level 3 Offices', location: 'Level 3' },
    { name: 'Basement Car Park', location: 'Basement' },
    { name: 'Atrium', location: 'Ground Floor' },
    { name: 'Loading Dock', location: 'Rear' },
  ],
  'Aquatic Centre': [
    { name: 'Pool Entry Gate', location: 'Main Entry' },
    { name: 'Changerooms', location: 'Ground Floor' },
    { name: 'Pool Deck', location: 'Pool Area' },
    { name: 'Plant Room', location: 'Basement' },
  ],
  'Council Depot': [
    { name: 'Entry Gate', location: 'Front Gate' },
    { name: 'Workshop Bay 1', location: 'Workshop' },
    { name: 'Vehicle Storage', location: 'Rear' },
    { name: 'Fuel Storage', location: 'Side Yard' },
    { name: 'Security Office', location: 'Admin Building' },
  ],
  'Parramatta Square': [
    { name: 'Main Entry', location: 'Ground Floor' },
    { name: 'Entry Plaza', location: 'External' },
    { name: 'Level 1 Lobby', location: 'Level 1' },
    { name: 'Car Park', location: 'Basement' },
  ],
  'River Walk': [
    { name: 'North Path Entry', location: 'North' },
    { name: 'River Promenade', location: 'River Side' },
    { name: 'South Park Exit', location: 'South' },
  ],
};

function NewTourModal({ onSave, onClose }: { onSave: (tour: Tour) => void; onClose: () => void }) {
  const [officer, setOfficer] = useState('');
  const [site, setSite] = useState(SITES[0]);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  async function handleSave() {
    if (!officer.trim()) { toast('Officer name is required', 'error'); return; }
    setSaving(true);
    const tok = localStorage.getItem('lemai_token') || '';
    const presets = CHECKPOINT_PRESETS[site] || [];
    const checkpoints = presets.map((p, i) => ({
      id: `CP-${Date.now()}-${i}`,
      name: p.name,
      location: p.location,
      completed: false,
    }));
    try {
      const r = await fetch('/api/tours', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ officer, site, startTime, status: 'in-progress', checkpoints }),
      });
      if (r.ok) {
        const tour = await r.json();
        onSave(tour);
        toast(`Tour started: ${site}`, 'success');
        onClose();
      } else {
        toast('Failed to create tour', 'error');
      }
    } catch {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '480px', maxWidth: '95vw' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#e5e5e5' }}>Start Guard Tour</h2>
            <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#555555' }}>Checkpoints loaded from site template</p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '4px', color: '#555555', width: '32px', height: '32px', fontSize: '16px', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#555555', marginBottom: '5px', fontWeight: 500 }}>Officer Name *</label>
            <input value={officer} onChange={e => setOfficer(e.target.value)} placeholder="e.g. Officer J. Chen"
              style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px', padding: '8px 10px', color: '#e5e5e5', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#555555', marginBottom: '5px', fontWeight: 500 }}>Site</label>
            <select value={site} onChange={e => setSite(e.target.value)}
              style={{ width: '100%', background: '#111111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px', padding: '8px 10px', color: '#e5e5e5', fontSize: '13px', outline: 'none' }}>
              {SITES.map(s => <option key={s} style={{ background: '#111111' }}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: '#555555', marginBottom: '5px', fontWeight: 500 }}>Start Time</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px', padding: '8px 10px', color: '#e5e5e5', fontSize: '13px', outline: 'none' }} />
          </div>
          {CHECKPOINT_PRESETS[site] && (
            <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '4px', padding: '10px 12px', fontSize: '11px', color: '#555555' }}>
              <strong style={{ color: '#93c5fd' }}>{CHECKPOINT_PRESETS[site].length} checkpoints</strong> will be loaded:{' '}
              {CHECKPOINT_PRESETS[site].map(c => c.name).join(' · ')}
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#555555', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 24px', borderRadius: '4px', border: 'none', background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', fontSize: '12px', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Starting…' : '▶ Start Tour'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuardTours() {
  const { toast } = useToast();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Tour | null>(null);
  const [filter, setFilter] = useState<'all' | 'in-progress' | 'completed' | 'overdue' | 'missed'>('all');
  const [showNew, setShowNew] = useState(false);
  const [completingCp, setCompletingCp] = useState<string | null>(null);

  const tok = localStorage.getItem('lemai_token') || '';

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/tours', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data: Tour[]) => {
        setTours(data);
        // Keep selected in sync
        if (selected) {
          const updated = data.find(t => t.id === selected.id);
          if (updated) setSelected(updated);
        } else if (data.length) {
          setSelected(data[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tok]);

  useEffect(() => { load(); }, []);

  // Auto-refresh every 30s for live updates
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  async function completeCheckpoint(tourId: string, cpId: string) {
    setCompletingCp(cpId);
    try {
      const r = await fetch(`/api/tours/${tourId}/checkpoint/${cpId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (r.ok) {
        const result = await r.json();
        // Update local state optimistically
        setTours(prev => prev.map(t => {
          if (t.id !== tourId) return t;
          const updated = {
            ...t,
            completionPct: result.completionPct,
            status: result.status,
            checkpoints: t.checkpoints.map(cp =>
              cp.id === cpId ? { ...cp, completed: true, completedAt: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) } : cp
            ),
          };
          if (selected?.id === tourId) setSelected(updated);
          return updated;
        }));
        if (result.completionPct === 100) toast(`Tour completed — ${result.completionPct}% done`, 'success');
        else toast(`Checkpoint recorded`, 'success');
      }
    } catch {
      toast('Failed to record checkpoint', 'error');
    } finally {
      setCompletingCp(null);
    }
  }

  async function deleteTour(id: string) {
    const r = await fetch(`/api/tours/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
    if (r.ok) {
      setTours(prev => prev.filter(t => t.id !== id));
      if (selected?.id === id) setSelected(null);
      toast('Tour removed', 'info');
    }
  }

  const filtered = tours.filter(t => filter === 'all' || t.status === filter);

  const counts = {
    active:    tours.filter(t => t.status === 'in-progress').length,
    completed: tours.filter(t => t.status === 'completed').length,
    scheduled: tours.filter(t => t.status === 'scheduled').length,
    overdue:   tours.filter(t => t.status === 'overdue' || t.status === 'missed').length,
  };

  return (
    <Layout title="Guard Tours" subtitle="Patrol scheduling, checkpoint scanning and compliance tracking">
      {showNew && <NewTourModal onSave={t => { setTours(prev => [t, ...prev]); setSelected(t); }} onClose={() => setShowNew(false)} />}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { l: 'In Progress', v: String(counts.active),    c: '#3b82f6', icon: '🚶' },
          { l: 'Completed',   v: String(counts.completed), c: '#22c55e', icon: '✅' },
          { l: 'Scheduled',   v: String(counts.scheduled), c: '#555555', icon: '📅' },
          { l: 'Overdue',     v: String(counts.overdue),   c: '#ef4444', icon: '⚠️' },
        ].map(s => (
          <div key={s.l} style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: '#555555' }}>{s.l}</span>
              <span style={{ fontSize: '18px' }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '14px' }}>
        {/* Tour List */}
        <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Filter + Start */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              {(['all', 'in-progress', 'completed', 'overdue'] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{
                  padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                  cursor: 'pointer', border: 'none', transition: 'all 0.15s',
                  background: filter === f ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: filter === f ? '#3b82f6' : '#555555',
                }}>
                  {f === 'in-progress' ? 'Active' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={load} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #1a1a1a', background: 'transparent', color: '#555555', fontSize: '11px', cursor: 'pointer' }}>
                Refresh
              </button>
              <button onClick={() => setShowNew(true)} style={{ padding: '5px 14px', borderRadius: '7px', border: 'none', background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                + Start Tour
              </button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555555', fontSize: '12px' }}>Loading tours…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#444444', fontSize: '13px' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🚶</div>
                No tours found. Click <strong style={{ color: '#22c55e' }}>+ Start Tour</strong> to begin a patrol.
              </div>
            )}
            {!loading && filtered.map(tour => (
              <div key={tour.id}
                onClick={() => setSelected(tour)}
                style={{
                  padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer',
                  background: selected?.id === tour.id ? 'rgba(59,130,246,0.06)' : 'transparent',
                  borderLeft: `3px solid ${STATUS_COLOR[tour.status] || '#555555'}`,
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#e5e5e5', marginBottom: '3px' }}>
                      {tour.site} — {tour.officer}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '9px', padding: '2px 7px', borderRadius: '4px', background: STATUS_BG[tour.status] || 'rgba(100,116,139,0.1)', color: STATUS_COLOR[tour.status] || '#555555', fontWeight: 700 }}>
                        {tour.status}
                      </span>
                      <span style={{ fontSize: '10px', color: '#555555' }}>
                        {tour.checkpoints.filter(c => c.completed).length}/{tour.checkpoints.length} checkpoints
                      </span>
                      <span style={{ fontSize: '10px', color: '#444444', fontFamily: 'monospace' }}>
                        {tour.id}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: '3px', background: 'rgba(255,255,255,0.07)', borderRadius: '2px' }}>
                      <div style={{ height: '100%', width: `${tour.completionPct}%`, background: STATUS_COLOR[tour.status] || '#555555', borderRadius: '2px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '11px', color: '#555555' }}>Start: {tour.startTime}</div>
                    {tour.endTime && <div style={{ fontSize: '10px', color: '#22c55e', marginTop: '2px' }}>Done: {tour.endTime}</div>}
                    <div style={{ fontSize: '11px', fontWeight: 700, color: STATUS_COLOR[tour.status] || '#555555', marginTop: '2px' }}>
                      {tour.completionPct}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tour Detail */}
        {selected ? (
          <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 3px', fontSize: '13px', fontWeight: 600, color: '#e5e5e5' }}>{selected.site}</h3>
                <div style={{ fontSize: '10px', color: '#555555' }}>{selected.officer} · {selected.id}</div>
              </div>
              <button onClick={() => deleteTour(selected.id)} style={{ padding: '3px 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '5px', color: '#ef4444', fontSize: '10px', cursor: 'pointer' }}>Remove</button>
            </div>

            {/* Summary */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                {[
                  { l: 'Status',   v: selected.status,          c: STATUS_COLOR[selected.status] || '#555555' },
                  { l: 'Start',    v: selected.startTime,        c: '#94a3b8' },
                  { l: 'Progress', v: `${selected.completionPct}%`, c: STATUS_COLOR[selected.status] || '#555555' },
                ].map(m => (
                  <div key={m.l} style={{ background: '#0d0d0d', borderRadius: '7px', padding: '7px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', color: '#555555', marginBottom: '2px' }}>{m.l}</div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: m.c }}>{m.v}</div>
                  </div>
                ))}
              </div>
              {/* Full progress bar */}
              <div style={{ marginTop: '10px', height: '6px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px' }}>
                <div style={{ height: '100%', width: `${selected.completionPct}%`, background: STATUS_COLOR[selected.status] || '#555555', borderRadius: '3px', transition: 'width 0.5s' }} />
              </div>
            </div>

            {/* Checkpoints */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              <div style={{ fontSize: '10px', color: '#555555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                Checkpoints — {selected.checkpoints.filter(c => c.completed).length}/{selected.checkpoints.length}
              </div>
              {selected.checkpoints.map((cp, i) => (
                <div key={cp.id} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${cp.completed ? '#22c55e' : cp.overdue ? '#ef4444' : 'rgba(255,255,255,0.12)'}`,
                      background: cp.completed ? 'rgba(34,197,94,0.15)' : cp.overdue ? 'rgba(239,68,68,0.1)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                    }}>
                      {cp.completed ? '✓' : cp.overdue ? '!' : String(i + 1)}
                    </div>
                    {i < selected.checkpoints.length - 1 && (
                      <div style={{ width: '1px', height: '16px', background: cp.completed ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.07)', marginTop: '2px' }} />
                    )}
                  </div>
                  <div style={{ flex: 1, paddingBottom: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', gap: '8px' }}>
                      <div>
                        <span style={{ fontSize: '12px', color: cp.completed ? '#e2e8f0' : '#94a3b8', fontWeight: cp.completed ? 600 : 400 }}>{cp.name}</span>
                        <span style={{ fontSize: '10px', color: '#555555', marginLeft: '6px' }}>{cp.location}</span>
                      </div>
                      {cp.completedAt && <span style={{ fontSize: '10px', color: '#555555', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{cp.completedAt}</span>}
                    </div>
                    {!cp.completed && selected.status === 'in-progress' && (
                      <button
                        onClick={() => completeCheckpoint(selected.id, cp.id)}
                        disabled={completingCp === cp.id}
                        style={{
                          padding: '3px 10px', borderRadius: '5px', border: '1px solid rgba(34,197,94,0.3)',
                          background: 'rgba(34,197,94,0.08)', color: '#22c55e', fontSize: '10px',
                          fontWeight: 700, cursor: completingCp === cp.id ? 'wait' : 'pointer', marginTop: '3px',
                        }}
                      >
                        {completingCp === cp.id ? '…' : '✓ Mark Complete'}
                      </button>
                    )}
                    {cp.overdue && !cp.completed && (
                      <div style={{ fontSize: '9px', color: '#ef4444', background: 'rgba(239,68,68,0.08)', padding: '2px 7px', borderRadius: '5px', display: 'inline-block', marginTop: '3px' }}>Overdue</div>
                    )}
                    {!cp.completed && !cp.overdue && selected.status === 'in-progress' && i === selected.checkpoints.filter(c => c.completed).length && (
                      <div style={{ fontSize: '9px', color: '#3b82f6', background: 'rgba(59,130,246,0.1)', padding: '2px 7px', borderRadius: '5px', display: 'inline-block', marginTop: '3px' }}>Next</div>
                    )}
                  </div>
                </div>
              ))}

              {selected.checkpoints.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#444444', fontSize: '12px' }}>No checkpoints defined</div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '10px', color: '#444444' }}>
            <div style={{ fontSize: '32px' }}>🚶</div>
            <div style={{ fontSize: '13px' }}>Select a tour to view checkpoints</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
